<template lang="pug">
.realtime-editor

  //- Left: collapsible component sidebar
  .component-sidebar(:class="sidebarOpen ? 'sidebar-open' : 'sidebar-collapsed'")
    .sidebar-header
      v-btn(icon small @click="sidebarOpen = !sidebarOpen" :title="sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'")
        v-icon(small) {{ sidebarOpen ? 'chevron_left' : 'chevron_right' }}
    v-list(v-show="sidebarOpen" dense nav)
      v-list-item(
        v-for="(comp, i) in components"
        :key="comp.id"
        :input-value="i === activeTabIndex"
        active-class="sidebar-item--active"
        @click="setActiveComponent(comp.id)"
      )
        v-list-item-content
          v-list-item-title.tab-label {{ comp.title }}

  //- Right: carousel + table
  .editor-main
    template(v-if="displayedComponent")
      .preview-section
        .section-label.px-3.pt-3
          .d-flex.align-center
            span.overline Preview
            template(v-if="isLoading")
              v-spacer
              span.caption.grey--text.mr-1 Rendering previews…
              v-progress-circular.mr-2(indeterminate size="14" width="2" color="grey")
          .caption.grey--text.pb-1(v-if="isLoading") Data edits will be reflected once previews finish rendering.
        v-progress-linear(
          :value="carouselProgress"
          :active="carouselProgress < 100"
          height="2"
          color="primary"
        )
        card-carousel(
          v-for="comp in components"
          v-show="displayedComponent && comp.id === displayedComponent.id"
          :key="comp.id"
          ref="carousels"
          :game="game"
          :component="comp"
          :selected-index="selectedIndex"
          :paused="!active || !displayedComponent || comp.id !== displayedComponent.id"
          @select="onCardSelect"
          @progress="onCarouselProgress(comp.id, $event)"
        )

      v-divider

      .data-section
        .section-label.overline.px-3.pt-2.d-flex.align-center
          span Data
          v-icon.ml-2.mr-1(small color="warning") mdi-alert
          span.formula-warning Editing cells will replace any spreadsheet formulas with their current plain-text value.
        .data-table-wrap(v-if="hasSheet")
          v-data-table(
            :headers="tableHeaders"
            :items="tableRows"
            :item-class="tableRowClass"
            dense
            hide-default-footer
            disable-pagination
            class="elevation-0"
            @click:row="item => selectRow(item.__rowIndex)"
          )
            template(v-slot:[`item.__rowIndex`]="{ item }")
              span.row-num {{ item.__rowIndex + 1 }}
            template(v-for="col in headers" v-slot:[`item.${col}`]="{ item }")
              v-edit-dialog(
                v-if="item.__rowIndex === selectedIndex"
                :key="col"
                @save="saveCell(item.__rowIndex, col, item[col])"
                large
                save-text="Save"
                cancel-text="Cancel"
              )
                span.cell-value {{ item[col] }}
                template(v-slot:input)
                  v-text-field(
                    v-model="item[col]"
                    dense
                    outlined
                    single-line
                    :label="col"
                    class="mt-3"
                  )
              span.cell-value(v-else) {{ item[col] }}
          .add-row-bar.px-2.py-1
            v-tooltip(bottom :disabled="headers.length > 0")
              template(v-slot:activator="{ on }")
                span(v-on="on")
                  v-btn(small text @click="addRow" :loading="addingRow" :disabled="headers.length === 0")
                    v-icon(left small) add
                    | Add Row
              span Add column headers to your spreadsheet first

        .pa-3.grey--text(v-else) Link a spreadsheet to this component to see its data here.

    .pa-4.grey--text(v-else) Select a component on the left to get started.
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import CardCarousel from './CardCarousel.vue'

export default {
  props: ['game', 'components', 'active'],

  components: { CardCarousel },

  created() {
    this._applyPendingOnIdle = false
  },

  data() {
    return {
      selectedIndex: 0,
      addingRow: false,
      sidebarOpen: true,
      carouselProgress: 100,
      localComponentId: null,
      pendingComponentId: null,
    }
  },

  computed: {
    ...mapGetters([
      'worksheetPropertyNames',
      'worksheetPropertyValues',
      'findSpreadsheetWorksheet',
      'activeComponent',
    ]),

    isLoading() {
      return this.carouselProgress < 100
    },

    // Decoupled from Vuex activeComponent while a render is in progress.
    // Changes from the Layer Editor are held in pendingComponentId and applied
    // once the carousel finishes loading.
    displayedComponent() {
      const id = this.localComponentId
      return id ? (this.components.find(c => c.id === id) || null) : this.activeComponent
    },

    activeTabIndex() {
      if (!this.displayedComponent) return -1
      return this.components.findIndex(c => c.id === this.displayedComponent.id)
    },

    hasSheet() {
      return this.displayedComponent &&
        this.displayedComponent.spreadsheetId &&
        this.displayedComponent.worksheetId
    },

    headers() {
      if (!this.hasSheet) return []
      return this.worksheetPropertyNames(
        this.displayedComponent.spreadsheetId,
        this.displayedComponent.worksheetId
      )
    },

    tableHeaders() {
      return [
        { text: '#', value: '__rowIndex', sortable: false, width: 40 },
        ...this.headers.map(h => ({ text: h, value: h, sortable: false }))
      ]
    },

    tableRows() {
      if (!this.hasSheet) return []
      const rows = this.worksheetPropertyValues(
        this.displayedComponent.spreadsheetId,
        this.displayedComponent.worksheetId
      )
      return rows.map((row, i) => {
        const obj = { __rowIndex: i }
        this.headers.forEach((h, j) => { obj[h] = row[j] || '' })
        return obj
      })
    },

    worksheetTitle() {
      if (!this.hasSheet) return ''
      const ws = this.findSpreadsheetWorksheet(
        this.displayedComponent.spreadsheetId,
        this.displayedComponent.worksheetId
      )
      return ws ? ws.title : ''
    },
  },

  watch: {
    active(isActive) {
      if (!isActive) {
        // Abort any deferred switch — it will be re-evaluated on return.
        this._applyPendingOnIdle = false
        if (this.isLoading) this.carouselProgress = 100
      } else if (this.pendingComponentId) {
        // Returning to the tab with a deferred switch waiting.
        if (!this.isLoading) {
          this.localComponentId = this.pendingComponentId
          this.pendingComponentId = null
        } else {
          // Still rendering — apply once this render batch finishes.
          this._applyPendingOnIdle = true
        }
      }
    },

    activeComponent(comp) {
      if (!comp) return
      if (this.isLoading) {
        // Defer the switch until the current render batch finishes.
        this.pendingComponentId = comp.id
      } else {
        this.localComponentId = comp.id
        this.pendingComponentId = null
      }
    },

    isLoading(loading) {
      // Only apply a deferred switch when we're returning from the Layer view,
      // not when a cell-edit render cycle happens to finish.
      if (!loading && this.pendingComponentId && this._applyPendingOnIdle) {
        this._applyPendingOnIdle = false
        this.localComponentId = this.pendingComponentId
        this.pendingComponentId = null
      }
    },

    displayedComponent(comp) {
      // Seed carouselProgress from the newly visible carousel so the progress
      // bar reflects its actual state rather than the previous component's.
      this.$nextTick(() => {
        const carousel = this._activeCarousel()
        this.carouselProgress = carousel ? carousel.renderProgress : 100
      })
    },

    components(newList) {
      if (this.activeTabIndex < 0 && newList.length > 0) {
        this.setActiveComponent(newList[0].id)
        return
      }
      const comp = this.displayedComponent
      if (comp && comp.spreadsheetId) {
        this.refreshSheetIndex(comp.spreadsheetId)
      }
    }
  },

  methods: {
    ...mapActions(['googleUpdateSheetRow', 'googleAppendSheetRow', 'refreshSheetIndex', 'setActiveComponent']),

    tableRowClass(item) {
      return item.__rowIndex === this.selectedIndex ? 'rt-selected-row' : ''
    },

    _activeCarousel() {
      const carousels = this.$refs.carousels || []
      const id = this.displayedComponent?.id
      return carousels.find(c => c.$props.component?.id === id) || null
    },

    onCarouselProgress(compId, value) {
      if (this.displayedComponent?.id === compId) this.carouselProgress = value
    },

    onCardSelect(index) {
      this.selectedIndex = index
      this.$nextTick(() => {
        const el = this.$el.querySelector('.rt-selected-row')
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        const carousel = this._activeCarousel()
        if (carousel) carousel.scrollToCard(index)
      })
    },

    selectRow(index) {
      this.selectedIndex = index
      this.$nextTick(() => {
        const carousel = this._activeCarousel()
        if (carousel) carousel.scrollToCard(index)
      })
    },

    async addRow() {
      this.addingRow = true
      try {
        await this.googleAppendSheetRow({
          spreadsheetId: this.displayedComponent.spreadsheetId,
          worksheetTitle: this.worksheetTitle,
          values: this.headers.map(() => '')
        })
        await this.refreshSheetIndex(this.displayedComponent.spreadsheetId)
      } finally {
        this.addingRow = false
      }
    },

    async saveCell(rowIndex, column, value) {
      const { spreadsheetId, worksheetId } = this.displayedComponent
      const rows = this.worksheetPropertyValues(spreadsheetId, worksheetId)
      const updatedRow = [...(rows[rowIndex] || [])]
      const colIndex = this.headers.indexOf(column)
      if (colIndex === -1) return
      updatedRow[colIndex] = value

      // Patch locally first so the preview re-renders immediately.
      // rowIndex + 1 because row 0 in the cache is the header row.
      this.$store.commit('patchCacheCell', {
        key: `${spreadsheetId}-${worksheetId}`,
        rowIndex: rowIndex + 1,
        colIndex,
        value
      })

      await this.googleUpdateSheetRow({
        spreadsheetId,
        worksheetTitle: this.worksheetTitle,
        rowNumber: rowIndex + 2,
        values: updatedRow
      })
    }
  }
}
</script>

<style>
.theme--light .rt-selected-row { background: rgba(25, 118, 210, 0.1) !important; }
.theme--dark  .rt-selected-row { background: rgba(100, 181, 246, 0.15) !important; }
.data-table-wrap tbody tr { cursor: pointer; }

.data-table-wrap::-webkit-scrollbar { width: 6px; height: 6px; }
.data-table-wrap::-webkit-scrollbar-thumb { border-radius: 3px; background: rgba(128,128,128,0.4); }
.data-table-wrap .cell-value {
  display: block;
  width: 100%;
  min-height: 1.2em;
}

/* Let .data-table-wrap be the sole scroll root so sticky works cleanly */
.data-table-wrap .v-data-table__wrapper {
  overflow: visible !important;
}

/* border-collapse: collapse breaks position: sticky — switch to separate with 0 spacing */
.data-table-wrap table {
  border-collapse: separate !important;
  border-spacing: 0 !important;
  font-family: 'Roboto', sans-serif !important;
}

/* Sticky header — sticks within .data-table-wrap */
.data-table-wrap thead th {
  position: sticky;
  top: 0;
  z-index: 2;
}

/* Off-colour header background */
.theme--light .data-table-wrap thead th {
  background-color: #eaecf0 !important;
  border-bottom: 2px solid rgba(0,0,0,0.18) !important;
}
.theme--dark .data-table-wrap thead th {
  background-color: #2a2d31 !important;
  border-bottom: 2px solid rgba(255,255,255,0.15) !important;
}
</style>

<style scoped>
.realtime-editor {
  display: flex;
  flex-direction: row;
  height: calc(100vh - 210px);
  min-height: 400px;
  overflow: hidden;
}
.component-sidebar {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width 0.2s ease;
  border-right: 1px solid rgba(0,0,0,0.12);
}
.theme--dark .component-sidebar { border-right-color: rgba(255,255,255,0.12); }
.sidebar-open     { width: 180px; }
.sidebar-collapsed { width: 36px; }
.sidebar-header { padding: 4px; flex-shrink: 0; }
.sidebar-loading { display: flex; align-items: center; }
.sidebar-item--active { background: rgba(0,0,0,0.08) !important; }
.theme--dark .sidebar-item--active { background: rgba(255,255,255,0.1) !important; }
.tab-label {
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.editor-main {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.preview-section { flex-shrink: 0; }
.data-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.data-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
.add-row-bar { border-top: 1px solid rgba(0,0,0,0.08); }
.formula-warning {
  font-size: 0.72rem;
  text-transform: none;
  letter-spacing: 0;
  color: #f57c00;
  font-weight: 400;
}
.row-num {
  cursor: pointer;
  display: inline-block;
  width: 100%;
  color: rgba(128,128,128,0.8);
  font-size: 0.75rem;
  user-select: none;
}
.row-num:hover { color: inherit; }
</style>
